import SectionWrapper from 'components/SectionWrapper/SectionWrapper';
import React, { createRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { I18n } from 'core';
import { navigate } from 'navigation';
import { LoadingButton } from 'components';
import Button from 'components/Button/Button';
import customToast from 'components/CustomToast/CustomToast';
import { toast } from 'react-toastify';

import FullPageSyncLoader from 'components/FullPageSyncLoader';
import { useFetchCommunity } from 'hooks';
import CommunitiesServiceIntance, { CommunitiesService } from 'services/remote/communities-service';
import { CommunityAPII, CommunityModel } from 'types/models/community';
import { CUSTOMIZABLE_IMAGE_TYPE_VALUES, CustomizableImage, CustomizableImageType } from 'types/models';
import { CommunityCustomOnboardingServiceInstance } from 'services';
import styles from './comunidades-custom-onboarding-process-page.module.scss';

import { ComunidadesCustomOnboardingInformeAutomatico } from './components/comunidades-custom-onboarding-informe-automatico';
import { ComunidadesCustomOnboardingImageForm } from './components';

interface CustomImagesFormFieldsI {
  imageFormTitle: string;
  addImageText: string;
  imageNote: string;
  buttonText: string;
  imageType: CustomizableImageType;
  images: CustomizableImage[];
}

export type SaveDataHandle = {
  getData: () => void;
};

export const ComunidadesCustomOnboardingProcessPageInformeAutomatico = (): JSX.Element => {
  const { communityID } = useParams<{ communityID: string }>();

  const [communityCustomOnboarding, setCommunityCustomOnboarding] = useState<CommunityModel | undefined>(undefined);

  const customOnboardingRef = createRef<SaveDataHandle>();

  const [loading, setLoading] = useState<boolean>(false);
  const [customImagesFormFields, setCustomImagesFormFields] = useState<CustomImagesFormFieldsI[]>([]);

  const getImageInputTexts = (
    imageType: CustomizableImageType,
  ): { imageFormTitle: string; addImageText: string; imageNote: string; buttonText: string } => {
    switch (imageType) {
      case CustomizableImageType.LOGO:
        return {
          imageFormTitle: I18n.t('ComunidadesCustomOnboardingProcess.form.logo.title'),
          addImageText: I18n.t('ComunidadesCustomOnboardingProcess.form.logo.text'),
          imageNote: I18n.t('ComunidadesCustomOnboardingProcess.form.logo.note'),
          buttonText: I18n.t('ComunidadesCustomOnboardingProcess.form.logo.button'),
        };
      case CustomizableImageType.IMAGE_STEP_01:
        return {
          imageFormTitle: I18n.t('ComunidadesCustomOnboardingProcess.form.image01.title'),
          addImageText: I18n.t('ComunidadesCustomOnboardingProcess.form.image01.text'),
          imageNote: I18n.t('ComunidadesCustomOnboardingProcess.form.image01.note'),
          buttonText: I18n.t('ComunidadesCustomOnboardingProcess.form.image01.button'),
        };
      case CustomizableImageType.IMAGE_STEP_02:
        return {
          imageFormTitle: I18n.t('ComunidadesCustomOnboardingProcess.form.image02.title'),
          addImageText: I18n.t('ComunidadesCustomOnboardingProcess.form.image02.text'),
          imageNote: I18n.t('ComunidadesCustomOnboardingProcess.form.image02.note'),
          buttonText: I18n.t('ComunidadesCustomOnboardingProcess.form.image02.button'),
        };
      case CustomizableImageType.IMAGE_STEP_03:
        return {
          imageFormTitle: I18n.t('ComunidadesCustomOnboardingProcess.form.image03.title'),
          addImageText: I18n.t('ComunidadesCustomOnboardingProcess.form.image03.text'),
          imageNote: I18n.t('ComunidadesCustomOnboardingProcess.form.image03.note'),
          buttonText: I18n.t('ComunidadesCustomOnboardingProcess.form.image03.button'),
        };
      case CustomizableImageType.IMAGE_STEP_04:
        return {
          imageFormTitle: I18n.t('ComunidadesCustomOnboardingProcess.form.image04.title'),
          addImageText: I18n.t('ComunidadesCustomOnboardingProcess.form.image04.text'),
          imageNote: I18n.t('ComunidadesCustomOnboardingProcess.form.image04.note'),
          buttonText: I18n.t('ComunidadesCustomOnboardingProcess.form.image04.button'),
        };
      case CustomizableImageType.IMAGE_STEP_05:
        return {
          imageFormTitle: I18n.t('ComunidadesCustomOnboardingProcess.form.image05.title'),
          addImageText: I18n.t('ComunidadesCustomOnboardingProcess.form.image05.text'),
          imageNote: I18n.t('ComunidadesCustomOnboardingProcess.form.image05.note'),
          buttonText: I18n.t('ComunidadesCustomOnboardingProcess.form.image05.button'),
        };
      case CustomizableImageType.FOOTER_AUTOINFORME:
        return {
          imageFormTitle: I18n.t('ComunidadesCustomOnboardingProcess.form.footerAutoinforme.title'),
          addImageText: I18n.t('ComunidadesCustomOnboardingProcess.form.footerAutoinforme.text'),
          imageNote: I18n.t('ComunidadesCustomOnboardingProcess.form.footerAutoinforme.note'),
          buttonText: I18n.t('ComunidadesCustomOnboardingProcess.form.footerAutoinforme.button'),
        };
      default:
        return { imageFormTitle: ' ', addImageText: ' ', imageNote: ' ', buttonText: ' ' };
    }
  };
  const getCustomOnboardingData = async (id: string): Promise<void> => {
    setLoading(true);
    try {
      const getComunity = await new CommunitiesService().getCommunity(id);
      const customOnboarding = await CommunityCustomOnboardingServiceInstance.getCommunityCustomOnboarding(id);

      if (customOnboarding && getComunity) {
        setCommunityCustomOnboarding(getComunity);
        const imageForms = CUSTOMIZABLE_IMAGE_TYPE_VALUES.filter(
          (t) => t === CustomizableImageType.FOOTER_AUTOINFORME,
        ).map((imageType: CustomizableImageType): CustomImagesFormFieldsI => {
          const images = customOnboarding.imagenes.filter((imagen) => imagen.tipo === imageType);
          const { imageFormTitle, addImageText, imageNote, buttonText } = getImageInputTexts(imageType);
          return { imageFormTitle, addImageText, imageNote, buttonText, imageType, images };
        });
        setCustomImagesFormFields(imageForms);
      }
    } catch (error) {
      toast.error(I18n.t('ComunidadesCustomOnboardingProcess.errors.errorGettingCustomOnboardingData'));
    } finally {
      setLoading(false);
    }
  };

  const updateCustomOnboarding = async (communityCustomOnboardingUpdate: CommunityAPII): Promise<void> => {
    try {
      await CommunitiesServiceIntance.putCommunities(communityCustomOnboardingUpdate);
    } catch (error) {
      toast.error(I18n.t('ComunidadesCustomOnboardingProcess.errors.updating'));
    }
  };

  const updateExcedent = async (excedente: string): Promise<void> => {
    try {
      await CommunitiesServiceIntance.putCommunitiesExcedent(communityID, excedente);

      customToast.success(I18n.t('ComunidadesCustomOnboardingProcess.updateSuccess'));
    } catch (error) {
      toast.error(I18n.t('ComunidadesCustomOnboardingProcess.errors.updating'));
    }
  };

  useEffect(() => {
    if (!communityID) return;
    getCustomOnboardingData(communityID);
  }, []);
  const fetchCommunityData = useFetchCommunity(communityID);
  useEffect(() => {
    fetchCommunityData();
  }, [fetchCommunityData]);

  if (loading) return <FullPageSyncLoader icon />;

  return (
    <div className={styles.container}>
      <SectionWrapper customClassname={styles.containerConfirmacionMensaje}>
        <>
          {communityCustomOnboarding && (
            <ComunidadesCustomOnboardingInformeAutomatico
              ref={customOnboardingRef}
              communityId={communityID}
              communityModel={communityCustomOnboarding}
              saveDetails={(customOnboarding: CommunityAPII) => updateCustomOnboarding(customOnboarding)}
              saveLimiteExcedente={(Excedente: string) => updateExcedent(Excedente)}
            />
          )}
        </>
        <div className={styles.margenBotImagen}>
          {communityCustomOnboarding &&
            customImagesFormFields.map((imageFormField) => (
              <ComunidadesCustomOnboardingImageForm
                key={imageFormField.imageType}
                communitiyId={communityID}
                images={imageFormField.images}
                afterSubmitAction={getCustomOnboardingData}
                imageType={imageFormField.imageType}
                imageFormTitle={imageFormField.imageFormTitle}
                addImageText={imageFormField.addImageText}
                imageNote={imageFormField.imageNote}
                buttonText={imageFormField.buttonText}
              />
            ))}
        </div>
        <div className={styles.positionBotonoes}>
          <div className={styles.divBoton}>
            <Button
              type="button"
              onClick={(): Promise<void> => navigate('/tecnic/communities')}
              variant="secondary"
              text={I18n.t('Cancel')}
            />
            <LoadingButton type="submit" text={I18n.t('Save')} onClick={() => customOnboardingRef.current?.getData()} />
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
};
